<script setup>
const props = defineProps({
  heading: String,
  body: String,
  cta: Object,
  media: Object,
  postDate: String,
  parent: String,
  textColor: String,
  gatedContent: {
    iframe: String,
    buttonLabel: String,
    gatedTitle: String,
  },
});
const { isBlackText } = useTheme();
// `textColor` is a theme key (e.g. "white", "plum") where `<theme>-text` resolves
// to either black or white in Tailwind config.
const isHeaderBlack = computed(() => isBlackText(props.textColor));

const { isMobileOrTablet } = useDevice();
const { width: innerWidth } = useWindowSize();

const isMobile = computed(() => {
  if (process.server) return isMobileOrTablet;
  return innerWidth.value < 900;
});

const gatedOverlayRef = ref(null);

function handleButtonClick() {
  if (gatedOverlayRef.value) {
    gatedOverlayRef.value.openOverlay();
  }
}
</script>

<template>
  <section class="relative flex min-h-[60vh] w-full">
    <div
      class="hero-insight relative z-[1] h-auto"
      :data-section-theme="isHeaderBlack ? 'dark' : null"
      :class="`text-${textColor}-text transition-colors duration-300`"
    >
      <ui-bleed
        class="flex h-full flex-col gap-[var(--grid-gap)] pb-4 pt-80 md:pt-52 lg:flex-row"
      >
        <div class="content relative h-full w-full lg:w-[var(--cols-6)]">
          <span class="blurred absolute flex h-full w-full"></span>
          <div
            class="relative z-[2] flex h-full flex-col justify-between gap-[var(--grid-gap)] p-bleed"
          >
            <div class="breadcrumbs inline-flex">
              <ui-font-text v-if="parent" type="xs"> Article </ui-font-text>
              <ui-font-text v-if="parent" type="xs" class="opacity-50">
                \{{ parent }}
              </ui-font-text>
            </div>
            <div class="body grid gap-4">
              <h1>
                <component-element-highlighted-text
                  :highlighted-text="heading"
                  :has-color="false"
                  :type="isMobile ? 'l' : 'xxl'"
                  :has-scramble-effect="true"
                />
              </h1>
              <ui-font-text v-if="body" type="s">
                {{ body }}
              </ui-font-text>
            </div>

            <ui-font-text v-if="postDate" type="xs" class="opacity-50">
              Published {{ formatDate(postDate) }}
            </ui-font-text>
          </div>
          <ui-tag-corners />
        </div>
        <div class="mt-auto flex w-full flex-wrap gap-4 lg:w-[var(--cols-6)]">
          <ui-call-to-action
            v-if="cta"
            :cta="cta"
            :has-background="true"
            :invert-background="false"
            :has-icon="true"
          />
          <ui-call-to-action
            v-if="gatedContent?.buttonLabel && gatedContent?.iframe"
            :button="true"
            @click="handleButtonClick"
          >
            <ui-font-text type="s-bold" class="lowercase">
              {{ formatCTALabel(gatedContent.buttonLabel) }}
            </ui-font-text>
          </ui-call-to-action>
        </div>
      </ui-bleed>
    </div>
    <div v-if="media" class="absolute left-0 top-0 z-[0] h-full w-full">
      <component-common-media
        class="h-full w-full object-cover"
        :media="media"
        loading="eager"
      ></component-common-media>
    </div>
    <gated-overlay
      v-if="gatedContent?.iframe"
      ref="gatedOverlayRef"
      :gated-title="gatedContent.gatedTitle"
      :iframe="gatedContent.iframe"
    />
  </section>
</template>

<style scoped>
.blurred {
  -webkit-backdrop-filter: blur(24px);
  backdrop-filter: blur(24px);
}
</style>
